import * as Schema from "effect/Schema";

export const FEEDBACK_CATEGORIES = ["Bug", "Feature request", "General feedback"] as const;
export const FeedbackCategorySchema = Schema.Literals(FEEDBACK_CATEGORIES);
export type FeedbackCategory = typeof FeedbackCategorySchema.Type;

/**
 * Enforced by the client before it spends a round trip and by Connect before it spends a GitHub
 * call. One declaration, because two numbers that must agree are one number.
 */
export const FEEDBACK_SUMMARY_MAX_LENGTH = 200;
export const FEEDBACK_DETAILS_MAX_BYTES = 8 * 1_024;

/**
 * Feedback Screenshots (ADR-0137): images only, so the server can check what it is handed, and
 * small enough that three of them plus the text stay well under the Lambda Function URL's 6 MB
 * synchronous payload. The client reduces an image to fit before sending.
 */
export const FEEDBACK_SCREENSHOT_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** Characters base64 spends on `bytes` bytes. */
export const base64EncodedLength = (bytes: number): number => Math.ceil(bytes / 3) * 4;
/** Bytes a base64 string decodes to, read from its length and padding without decoding it. */
export const base64DecodedByteLength = (base64: string): number =>
  Math.max(
    0,
    Math.floor((base64.length * 3) / 4) -
      (base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0),
  );

export const FEEDBACK_SCREENSHOT_MAX_COUNT = 3;
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 1_024 * 1_024;
/** The text's own allowance plus three screenshots at their base64 size. */
export const FEEDBACK_REQUEST_MAX_BYTES =
  16 * 1_024 + FEEDBACK_SCREENSHOT_MAX_COUNT * base64EncodedLength(FEEDBACK_SCREENSHOT_MAX_BYTES);

/**
 * Where a report was started from, when not spontaneous (ADR-0138). A fixed set, so the field can
 * never carry free text into the issue.
 */
export const FEEDBACK_ORIGINS = ["toast", "thread-error", "crash"] as const;
export const FeedbackOriginSchema = Schema.Literals(FEEDBACK_ORIGINS);
export type FeedbackOrigin = typeof FeedbackOriginSchema.Type;

/** A failure the user chose to report, as "Report this problem" hands it to the feedback form. */
export interface ReportedFailure {
  readonly origin: FeedbackOrigin;
  /** Short and generic, from the kind of failure; never the raw message. */
  readonly summary: string;
  /** Exactly what the user was shown. */
  readonly message: string;
  /** When the failure was seen, ISO 8601, so a maintainer can find it in the logs. */
  readonly occurredAt?: string;
  /** The thread the failure concerns, whose Environment the Diagnostics Pointer names. */
  readonly threadRef?: { readonly environmentId: string; readonly threadId: string };
}

/** A Diagnostics Pointer names where diagnostics are, never their contents, so it stays small. */
export const FEEDBACK_DIAGNOSTICS_MAX_BYTES = 2 * 1_024;
export const FEEDBACK_DIAGNOSTICS_MAX_CLOUD_WORKSPACES = 2;

/**
 * What the submitter saw under "Include where diagnostics are stored", plus the Cloud Workspaces
 * it concerns. Connect adds each owned workspace's storage and log locations (ADR-0138).
 */
export const FeedbackDiagnosticsSchema = Schema.Struct({
  text: Schema.String,
  cloudWorkspaceIds: Schema.optionalKey(
    Schema.Array(Schema.String).check(
      Schema.isMaxLength(FEEDBACK_DIAGNOSTICS_MAX_CLOUD_WORKSPACES),
    ),
  ),
});
export type FeedbackDiagnostics = typeof FeedbackDiagnosticsSchema.Type;

export const FeedbackScreenshotContentTypeSchema = Schema.Literals(
  FEEDBACK_SCREENSHOT_CONTENT_TYPES,
);
/** One Feedback Screenshot as it crosses the wire: its declared type and its bytes in base64. */
export const FeedbackScreenshotSchema = Schema.Struct({
  contentType: FeedbackScreenshotContentTypeSchema,
  data: Schema.String,
});
export type FeedbackScreenshot = typeof FeedbackScreenshotSchema.Type;
const FeedbackScreenshotsSchema = Schema.Array(FeedbackScreenshotSchema).check(
  Schema.isMaxLength(FEEDBACK_SCREENSHOT_MAX_COUNT),
);

/**
 * What a client sends to `POST /v1/feedback`. `clientVersion` is the version the client shows: an
 * Application Release for Desktop, or a Production Promotion's version (or `preview`) for the
 * Runtime Web Client.
 */
export const FeedbackSubmissionSchema = Schema.Struct({
  category: FeedbackCategorySchema,
  summary: Schema.String,
  details: Schema.String,
  clientVersion: Schema.String,
  platform: Schema.String,
  idempotencyKey: Schema.String,
  screenshots: Schema.optionalKey(FeedbackScreenshotsSchema),
  origin: Schema.optionalKey(FeedbackOriginSchema),
  diagnostics: Schema.optionalKey(FeedbackDiagnosticsSchema),
});
export type FeedbackSubmission = typeof FeedbackSubmissionSchema.Type;

export const FeedbackDraftSchema = Schema.Struct({
  category: Schema.String,
  summary: Schema.String,
  details: Schema.String,
  screenshots: Schema.optionalKey(Schema.Array(FeedbackScreenshotSchema)),
  origin: Schema.optionalKey(FeedbackOriginSchema),
  diagnostics: Schema.optionalKey(FeedbackDiagnosticsSchema),
});
export type FeedbackDraft = typeof FeedbackDraftSchema.Type;

const FeedbackFieldErrorsSchema = Schema.Struct({
  category: Schema.optionalKey(Schema.String),
  summary: Schema.optionalKey(Schema.String),
  details: Schema.optionalKey(Schema.String),
  screenshots: Schema.optionalKey(Schema.String),
  diagnostics: Schema.optionalKey(Schema.String),
});
export type FeedbackFieldErrors = typeof FeedbackFieldErrorsSchema.Type;

/**
 * Why a submission was refused, for the one caller that must react rather than only read:
 * `sign-in-required` means the account credential is gone or rejected, and the Feedback panel
 * re-reads its account status so it stops presenting itself as signed in (#1008). Every other
 * refusal is carried by `message` alone.
 */
export const FeedbackFailureReasonSchema = Schema.Literals([
  "sign-in-required",
  "rejected",
  "unavailable",
]);
export type FeedbackFailureReason = typeof FeedbackFailureReasonSchema.Type;

/**
 * What a submission that failed for no reason we can name says.
 *
 * Declared once because two surfaces reach it independently: the shared failure mapper for an
 * unrecognized refusal, and the renderer for a bridge call that never returned a result at all.
 */
export const FEEDBACK_GENERIC_FAILURE_MESSAGE =
  "Could not send your feedback. Check your connection and try again.";

/**
 * What Connect answers a created report with. The counts are absent from a Connect that predates
 * Feedback Screenshots, which attached none.
 */
export const FeedbackCreatedResponseSchema = Schema.Struct({
  reference: Schema.String,
  screenshotsAttached: Schema.optionalKey(Schema.Number),
  screenshotsDropped: Schema.optionalKey(Schema.Number),
});
export type FeedbackCreatedResponse = typeof FeedbackCreatedResponseSchema.Type;

/**
 * Success carries a reference number and no link. The Feedback Issue lands in a private
 * repository the submitter cannot open, so a link would 404 for exactly the person handed it
 * (ADR-0105); maintainers reply to the account's email instead.
 */
export const FeedbackSubmitResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("invalid"),
    fieldErrors: FeedbackFieldErrorsSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("failed"),
    message: Schema.String,
    reason: FeedbackFailureReasonSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("created"),
    reference: Schema.String,
    /** A screenshot GitHub would not take is dropped rather than failing the report (ADR-0137). */
    screenshotsAttached: Schema.Number,
    screenshotsDropped: Schema.Number,
  }),
]);
export type FeedbackSubmitResult = typeof FeedbackSubmitResultSchema.Type;

export interface FeedbackBridge {
  readonly submit: (draft: FeedbackDraft) => Promise<FeedbackSubmitResult>;
  /**
   * Desktop only: the main window as a PNG, or null when there is no window to capture. The
   * caller hides its own feedback dialog first, so the capture shows what the report is about.
   */
  readonly captureWindow?: () => Promise<FeedbackScreenshot | null>;
}
